<template>
    <div>
        <div>
            <div class="d-flex pa-0 mb-0 mt-1">

                <!-- PARENT -->
                <v-card :color="mode==='create' ? 'grey darken-1' : 'grey darken-2'"
                        class="pa-2 mt-4 mb-1 align-self-center d-flex justify-space-between"
                        :min-width="mode==='create' ? 800 : 1050">

                    <!-- QUOTA NAME AND COUNTS -->
                    <div class="body-1 ml-1 align-self-center d-flex" style="width: 400px">
                        <div class="align-self-center">
                            {{ itemData.name.replace('_', ' ') }}
                        </div>
                        <div class="grey--text text--lighten-1 body-2 ml-3 align-self-center">
                            {{ parent.count }} / {{ parent.quota }}
                        </div>
                    </div>

                    <!-- ERROR --->
                    <div class="d-flex align-self-center">
                        <v-icon v-if="parent.error" class="ml-2 mr-2" color="yellow darken-2">
                            mdi-alert
                        </v-icon>

                        <div v-if="parent.error">
                            {{ parent.error }}
                        </div>
                    </div>

                    <div class="d-flex">

                        <!-- PERCENT TOGGLE -->
                        <v-btn :color="percentMode ? 'primary' : 'white'"
                               :text="!percentMode"
                               v-show="mode==='create'"
                               @click="percentMode=!percentMode"
                               small>
                            Toggle %
                        </v-btn>

                        <!-- BALANCE -->
                        <v-btn class="ml-1" v-show="mode==='create'" text @click="balanceClicked(parent)" small>Balance</v-btn>
                    </div>

                </v-card>

                <!-- DELETE ICON -->
                <v-btn icon class="align-self-center" v-if="mode==='create'">
                    <v-icon class="mt-3" color="grey lighten-1" @click="deleteQuotaClicked(parent)">mdi-delete
                    </v-icon>
                </v-btn>
            </div>

            <div v-for="child in parent.children" :key="child.id">

                <quota-leaf v-if="showChild(child)"
                            class="ml-5"
                            :percent-mode="percentMode"
                            :item-data="child"
                            :project="project"
                            :mode="mode"
                            :feasibility-running="feasibilityRunning"
                            :show-analyze-results="showAnalyzeResults"
                            @change="childChanged"
                ></quota-leaf>
            </div>
        </div>

        <json v-if="false" :data="parent"/>
    </div>

</template>

<script>

import QuotaLeaf from "./QuotaLeaf";
import Json from 'vue-json-pretty';
import EqualDivider from "../../ts/EqualDivider";
import QuotaProgress from "./QuotaProgress";

export default {

    components: {
        QuotaProgress,
        Json,
        QuotaLeaf
    },

    props: {

        itemData: {
            type: Object,
            required: true,
        },
        project: {
            type: Object,
            required: true,
        },

        mode: {
            type: String,
            required: true,
            validator: value => ['create', 'runtime'].includes(value)
        },

        feasibilityRunning: {
            type: Boolean,
            required: true,
            default: false,
        },

        showAnalyzeResults: {
            type: Boolean,
            required: true,
            default: false,
        },

        showHidden: {
            type: Boolean,
            required: true,
            default: false,
        }
    },

    created() {
        this.parent = Object.assign({}, this.itemData);
    },

    watch: {
        itemData(itemData) {
            this.parent = JSON.parse(JSON.stringify(itemData));
        }
    },

    data() {
        return {
            parent: null,
            percentMode: false,
        }
    },

    methods: {

        percentToggleOperated() {
            this.parent.error = null;
        },

        showChild(item) {
            if (this.showHidden === true) return true;
            return !(!item.enabled && this.mode === 'runtime');
        },

        enableToggled(itemData) {

            let item = this.findChild(itemData);

            item = Object.assign({}, item, itemData);

            this.validateParentTotal();

            this.emit();
        },

        findChild(itemData) {
            return this.parent.children.find(child => {

                if (itemData.option_unique_id) {
                    return child.option_unique_id === itemData.option_unique_id;
                } else {
                    return child.range_id === itemData.range_id;
                }
            });
        },

        validateParentTotal() {

            const total = this.sumChildren();

            if (total > this.project.target_completes) {
                this.parent.error = "Remove " + (total - this.project.target_completes).toLocaleString();
            }

            if (total < this.project.target_completes) {
                this.parent.error = "Add " + (this.project.target_completes - total).toLocaleString();
            }

            this.emit();
        },

        validateParentPercentTotal() {

            const total = this.sumChildrenPercents();

            if (total > 100) {
                this.parent.error = "Remove " + (total - 100).toLocaleString() + "%";
            }

            if (total < 100) {
                this.parent.error = "Add " + (100 - total).toLocaleString() + "%";
            }

            this.emit();
        },

        childChanged(childData) {

            let child = this.findChild(childData);

            child.soft = childData.soft;
            child.enabled = childData.enabled;
            child.quota = parseInt(childData.quota);
            child.quota_percent = childData.quota_percent;

            if (this.percentMode) {
                this.validateParentPercentTotal();
            } else {
                this.validateParentTotal();
            }
            this.emit();
        },

        sumChildren() {

            let total = 0;

            this.parent.children
                .filter(item => item.enabled)
                .forEach(item => {

                    this.parent.error = null;

                    let quota = parseInt(item.quota);

                    if (isNaN(quota)) {
                        this.parent.error = "Invalid value!";
                        quota = 0;
                    }

                    total += quota;
                });

            this.parent.quota = total;

            return total;
        },

        sumChildrenPercents() {

            let total = 0;

            this.parent.children
                .filter(item => item.enabled)
                .forEach(item => {

                    this.parent.error = null;

                    let quota = parseFloat(item.quota_percent);

                    if (isNaN(quota)) {
                        this.parent.error = "Invalid value!";
                        quota = 0;
                    }

                    total += quota;
                });

            this.parent.quota_percent = total;

            return total;
        },

        balanceClicked() {

            const enabledChildren = this.parent.children.filter(child => child.enabled);

            const result = new EqualDivider(this.project.target_completes, enabledChildren.length).getResults();

            let index = 0;

            let parent = JSON.parse(JSON.stringify(this.parent));

            parent.error = null;

            parent.children.forEach(item => {
                if (item.enabled) {

                    item.quota_percent = Math.round(result[index] / this.project.target_completes * 10000) / 100;
                    item.quota = result[index];
                    index++;
                }
            });

            this.parent = parent;

            this.sumChildren();
            this.emit();
        },

        deleteQuotaClicked(data) {
            this.$emit('delete', data);
        },

        emit() {
            this.$emit('change', this.parent);
        }
    }
}
</script>

<style scoped>

.active {
    background-color: white;
    opacity: 1;
}

</style>